import { Button, Card, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import HorizontalItem from 'components/container/HorizontalItem'
import InfoTip from 'components/InfoTip'
import Typoleton from 'components/container/Typoleton'
import VerticalItem from 'components/container/VerticalItem'
import { AssetData } from 'constants/assets'
import CoreTable from 'core/CoreTable'
import CoreToken from 'core/CoreToken'
import { assetService } from 'services/assetService'
import BorrowModal from 'components/Modal/BorrowModal'
import { useState } from 'react'
import { useModal } from 'react-modal-hook'
import usePoolTx from 'hooks/usePoolTx'
import RepayModal from 'components/Modal/RepayModal'
import { MarketResponse } from 'interfaces/contracts/edgePool'
import { PERCENT } from 'constants/number'
import { toA } from 'utils/display'
import { poolService } from 'services/poolService'
import { oracleStore, poolStore } from 'stores'
import { observer } from 'mobx-react-lite'
import TableRowMobile from 'components/container/TableRowMobile'

const BorrowMarketTable = observer(() => {
  const poolAddr = poolService.poolAddress
  const { marketInfo, borrowMarketList } = poolStore.getPool(poolAddr)
  const prices = oracleStore.getPrices(poolAddr)
  const { assets } = assetService
  const { sendBorrow, sendRepay } = usePoolTx()

  const [selectedAsset, setSelectedAsset] = useState<AssetData>()
  const [showWithdrawModal, hideWithdrawModal] = useModal(() => {
    if (!selectedAsset) return <></>

    return (
      <BorrowModal
        onClose={hideWithdrawModal}
        sendBorrow={sendBorrow}
        assetData={selectedAsset}
      />
    )
  }, [selectedAsset, sendBorrow])
  const [showRepayModal, hideRepayModal] = useModal(() => {
    if (!selectedAsset) return <></>

    return (
      <RepayModal
        onClose={hideRepayModal}
        sendRepay={sendRepay}
        assetData={selectedAsset}
      />
    )
  }, [selectedAsset, sendRepay, marketInfo])

  const handleOnBorrow = (assetData: AssetData) => {
    setSelectedAsset(assetData)
    showWithdrawModal()
  }
  const handleOnRepay = (assetData: AssetData) => {
    setSelectedAsset(assetData)
    showRepayModal()
  }

  const getRow = (_market: MarketResponse, responsive: boolean = false) => {
    const { underlying } = _market
    const { walletBorrowBalance, borrowLimit, config, borrowAPY, totalCredit } =
      marketInfo[underlying]

    const assetData = assets[underlying]
    const price = prices?.[underlying]
    const borrowBal = walletBorrowBalance
    const borrowingFactor = new BigNumber(config.borrow_factor).times(PERCENT)
    const totalCreditUSD = totalCredit.times(price)

    if (responsive) {
      const isLasted =
        borrowMarketList &&
        borrowMarketList.indexOf(_market) === borrowMarketList.length - 1
      return (
        <TableRowMobile key={underlying} border={!isLasted}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <CoreToken assetData={assetData} size={32} withName fontBold />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                variant="contained"
                onClick={() => handleOnBorrow(assetData)}
                sx={{ height: '40px', width: '100px' }}
              >
                Borrow
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleOnRepay(assetData)}
                sx={{ height: '40px', width: '100px' }}
              >
                Repay
              </Button>
            </Box>
          </Box>
          <HorizontalItem>
            <Typography variant="body1">Borrow APY</Typography>
            <Typography variant="body1">{toA(borrowAPY)}%</Typography>
          </HorizontalItem>
          <HorizontalItem>
            <Typography variant="body1">My borrow bal.</Typography>
            <Typography variant="body1">
              {`${toA(borrowBal)} ${assetData.symbol}`}
            </Typography>
          </HorizontalItem>
          <HorizontalItem>
            <Box sx={{ display: 'flex' }}>
              <Typography variant="body1">Borrow limit</Typography>
              <InfoTip kind="Borrow limit" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1">
                {toA(borrowLimit)} {assetData.symbol}
              </Typography>
              <InfoTip kind="Borrow limit" text={`${toA(borrowingFactor)}%`} />
            </Box>
          </HorizontalItem>
        </TableRowMobile>
      )
    }
    return [
      <Box display="flex" gap="12px">
        <CoreToken assetData={assetData} size={32} />
        <VerticalItem sx={{ alignItems: 'start' }}>
          <Typography variant="body1" fontWeight={600}>
            {assetData.symbol}
          </Typography>
          <Typography variant="caption">
            Available ${toA(totalCreditUSD)}
          </Typography>
        </VerticalItem>
      </Box>,
      <Typography variant="body1">{toA(borrowAPY)}%</Typography>,
      <VerticalItem>
        <Typography variant="body1">
          {`${toA(borrowBal)} ${assetData.symbol}`}
        </Typography>
        <Typography variant="caption">
          ${toA(price.times(borrowBal))}
        </Typography>
      </VerticalItem>,
      <VerticalItem>
        <Typography variant="body1">{`${toA(borrowLimit)} ${
          assetData.symbol
        }`}</Typography>
        <Typography variant="caption">{`${toA(
          borrowingFactor,
        )}% of borrowing power left`}</Typography>
      </VerticalItem>,
      <Box display="flex" gap="16px" justifyContent="center">
        <Button
          variant="contained"
          onClick={() => handleOnBorrow(assetData)}
          sx={{ height: '40px', width: '100px' }}
        >
          Borrow
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleOnRepay(assetData)}
          sx={{ height: '40px', width: '100px' }}
        >
          Repay
        </Button>
      </Box>,
    ]
  }

  return (
    <Card sx={{ border: 'none' }}>
      <Typography variant="h6" fontSize={20}>
        Borrow market
      </Typography>
      <br />
      <CoreTable
        headers={[
          <Typography variant="caption">Asset</Typography>,
          <Typography variant="caption">Borrow APY</Typography>,
          <Typography variant="caption">My borrow bal.</Typography>,
          <InfoTip kind="Borrow limit" text="Borrow limit" variant="caption" />,
          <></>,
        ]}
        rows={
          borrowMarketList?.map((market) => {
            return getRow(market)
          }) || Array(3).fill(LoadingTable)
        }
        responsive={borrowMarketList?.map((market) => {
          return getRow(market, true)
        })}
      />
    </Card>
  )
})
export default BorrowMarketTable

const LoadingTable = [
  <CoreToken size={32} />,
  <Typoleton variant="body1" />,
  <VerticalItem>
    <Typoleton variant="body1" />
    <Typoleton variant="caption" />
  </VerticalItem>,
  <VerticalItem>
    <Typoleton variant="body1" />
    <Typoleton variant="caption" />
  </VerticalItem>,
  <Box display="flex" gap="16px" justifyContent="center">
    <Button
      variant="contained"
      disabled
      sx={{ height: '40px', width: '100px' }}
    >
      Borrow
    </Button>
    <Button variant="outlined" disabled sx={{ height: '40px', width: '100px' }}>
      Repay
    </Button>
  </Box>,
]
